import { getSupabase } from '@/lib/supabase';
import { callRpc, describeError } from '@/api/resources';

/**
 * Application OTP: consent, step-up approval, document share.
 *
 * Not sign-in - Supabase Auth owns login codes. The difference matters: a login code proves
 * who is holding the phone, and one of these proves a FARMER agreed to something while a
 * field officer was standing in front of them.
 *
 * The browser never generates the code and never states where it goes. Issuing runs in the
 * `send-otp` edge function with the service role; the destination is read from the party's
 * own record, so a code cannot be diverted to the officer's own phone.
 */

export interface OtpChannel {
  channel: 'whatsapp' | 'sms' | 'email';
  destination_masked: string | null;
  available: boolean;
}

export interface IssuedOtp {
  otp_event_id: string;
  channel: string;
  destination_masked: string;
  expires_at: string;
  /** Further channels the same code went to (consent, migration 0064). */
  also?: Array<{ channel: OtpChannel['channel']; destination_masked: string; delivered: boolean; error?: string }>;
}

/** Which channels this party can actually be reached on, each with its masked number. */
export function otpChannels(partyId?: string) {
  return callRpc<OtpChannel[]>('fn_otp_channels', { p_party_id: partyId ?? null });
}

export async function sendOtp(input: {
  purpose: 'consent' | 'step_up' | 'document_share' | 'device_binding' | 'acknowledgement';
  channel: OtpChannel['channel'];
  /** Send the same code on these channels too. */
  alsoChannels?: Array<OtpChannel['channel']>;
  partyId?: string;
  linkedTable?: string;
  linkedId?: string;
}): Promise<IssuedOtp> {
  const { data, error } = await getSupabase().functions.invoke('send-otp', {
    body: {
      purpose: input.purpose,
      channel: input.channel,
      also_channels: input.alsoChannels ?? [],
      party_id: input.partyId ?? null,
      linked_entity_table: input.linkedTable ?? null,
      linked_entity_id: input.linkedId ?? null,
    },
  });
  if (error) {
    // functions.invoke hides the body on a non-2xx; the message the database wrote is the
    // only useful part ("No whatsapp contact is recorded for this party"), so dig it out.
    const detail = await readFunctionError(error);
    throw new Error(detail ?? 'The code could not be sent.');
  }
  const payload = data as IssuedOtp & { error?: string };
  if (payload?.error) throw new Error(payload.error);
  return payload;
}

export async function verifyOtp(otpEventId: string, code: string): Promise<{ verified: boolean; failure_reason: string | null }> {
  const rows = await callRpc<Array<{ verified: boolean; failure_reason: string | null }>>(
    'fn_verify_otp', { p_otp_event_id: otpEventId, p_code: code },
  );
  return rows?.[0] ?? { verified: false, failure_reason: 'otp_not_found' };
}

export function acknowledgeConsent(consentId: string, otpEventId: string) {
  return callRpc<string>('fn_acknowledge_consent', { p_consent_id: consentId, p_otp_event_id: otpEventId });
}

/** Plain-language reasons. The raw codes are for the log, not for a farmer's field officer. */
export const OTP_FAILURES: Record<string, string> = {
  otp_not_found: 'That code does not belong to this request. Send a new one.',
  otp_already_used: 'That code has already been used. Send a new one.',
  otp_expired: 'That code has expired. Send a new one.',
  otp_locked: 'Too many wrong attempts. Send a new code.',
  otp_cancelled: 'That code was cancelled. Send a new one.',
  otp_mismatch: 'That code is not right. Check it and try again.',
};

async function readFunctionError(error: unknown): Promise<string | null> {
  const context = (error as { context?: Response }).context;
  if (context && typeof context.json === 'function') {
    try {
      const body = await context.json() as { error?: string };
      if (body?.error) return body.error;
    } catch {
      // A non-JSON body tells us nothing useful; fall through to the generic message.
    }
  }
  return error instanceof Error ? describeError({ message: error.message }) : null;
}
